import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Link,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '-';
  return `${(Number(num) * 100).toFixed(2)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const NotificationHyperliquidWalletsCard = ({ hit }) => {
  const wallet = hit._source || {};
  const address = wallet.wallet_address || hit._id;
  const explorerUrl = address ? `https://hyperdash.info/trader/${address}` : null;
  const tokens = Array.isArray(wallet.unique_tokens) ? wallet.unique_tokens : [];

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card elevation={1} sx={{ width: '100%', backgroundColor: '#fafafa', overflow: 'visible' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-all' }}>
                {address || 'Unknown Wallet'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {wallet.chain && <Chip size="small" label={wallet.chain} variant="outlined" />}
              </Box>
            </Box>
            <Chip label={hit._score != null ? hit._score.toFixed(2) : '-'} size="small" variant="outlined" color="primary" />
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>PnL / Volume</Typography>
            <MetricRow label="Net PnL" value={formatCurrency(wallet.net_pnl_usd)} />
            <MetricRow label="Realized PnL" value={formatCurrency(wallet.realized_pnl_usd)} />
            <MetricRow label="Total Buy Volume" value={formatCurrency(wallet.total_buy_volume_usd)} />
            <MetricRow label="Total Sell Volume" value={formatCurrency(wallet.total_sell_volume_usd)} />
            <MetricRow label="Largest Trade" value={formatCurrency(wallet.largest_trade_usd)} />
            <MetricRow label="Avg Trade Size" value={formatCurrency(wallet.avg_trade_size_usd)} />
          </Paper>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Activity / Rates</Typography>
            <MetricRow label="Trade Count" value={formatNumber(wallet.trade_count, 0)} />
            <MetricRow label="Buys / Sells" value={`${formatNumber(wallet.buy_count, 0)} / ${formatNumber(wallet.sell_count, 0)}`} />
            <MetricRow label="Tokens Traded" value={formatNumber(wallet.tokens_traded, 0)} />
            <MetricRow label="Win Rate" value={formatPercent(wallet.win_rate)} />
            <MetricRow label="True Win Rate" value={formatPercent(wallet.true_win_rate)} />
            <MetricRow label="Winning / Closed Positions" value={`${formatNumber(wallet.winning_positions, 0)} / ${formatNumber(wallet.closed_positions, 0)}`} />
          </Paper>

          {tokens.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tokens (sample)</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {tokens.slice(0, 16).map((token) => (
                  <Chip key={token} size="small" label={token} variant="outlined" />
                ))}
              </Box>
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timestamps</Typography>
            <MetricRow label="First Trade" value={formatDate(wallet.first_trade_time)} />
            <MetricRow label="Last Trade" value={formatDate(wallet.last_trade_time)} />
            <MetricRow label="Updated" value={formatDate(wallet.updated_at)} />
          </Paper>

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.7rem' }}>
              {explorerUrl ? (
                <Link href={explorerUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  {address}
                </Link>
              ) : (
                address
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default NotificationHyperliquidWalletsCard;
